import axios from 'axios';
import { useAnalytics } from 'hooks/useAnalytics/useAnalytics';
import { useCustomNavigate } from 'hooks/useCustomNavigate';
import { useCallback, useContext, useState } from 'react';
import { PLEASE_TRY_AGAIN } from '../../constants/onboarding';
import { LOGIN_ROUTE } from '../../constants/routes';
import { UserContext } from '../../context/UserContext';
import { ory } from '../../ory.config';
import { useOry } from '../useOry';
import { OryFlowType } from '../useOry/useOry';

interface ILogoutFlowState {
    loading: boolean;
    error: string;
}

export const useLogoutFlow = () => {
    const { handleError } = useOry();
    const { clear } = useContext(UserContext);
    const { navigate, navigateWithRedirectTo } = useCustomNavigate();
    const { captureMantaCareEvent } = useAnalytics();
    const [state, setState] = useState<ILogoutFlowState>({
        loading: false,
        error: ''
    });

    /**
     * Set error in the state.
     */
    const setFlowError = useCallback(
        (error: string) => {
            setState(state => ({ ...state, error, loading: false }));
        },
        [setState]
    );

    /**
     * A common function to handle error in ory login flow.
     */
    const handleOryError = useCallback(
        (e: unknown) => {
            if (axios.isAxiosError(e)) {
                handleError(OryFlowType.LOGIN, undefined, setFlowError)(e);
            } else {
                setFlowError(PLEASE_TRY_AGAIN);
            }
            setState(state => ({ ...state, loading: false }));
        },
        [setFlowError, handleError]
    );

    const logout = async (isAutoLogout?: boolean) => {
        try {
            setState(state => ({ ...state, error: '', loading: true }));
            captureMantaCareEvent({ type: 'session', action: 'logout' });
            const { data: flow } = await ory.createBrowserLogoutFlow();
            await ory.updateLogoutFlow({
                token: flow.logout_token
            });
            clear();
            setState(state => ({ ...state, error: '', loading: false }));
            isAutoLogout ? navigateWithRedirectTo({ pathname: LOGIN_ROUTE }) : navigate(LOGIN_ROUTE);
        } catch (error) {
            clear();
            handleOryError(error);
        }
    };

    return { logout, loading: state.loading, error: state.error };
};
